import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { ArrowRight, Check, ShoppingBag, ShoppingCart, PlusCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import CommunitySection from "@/components/CommunitySection";

const STRIPE_LINKS = {
    "module-1": "https://buy.stripe.com/7sYbJ14Q305E8szc6Y1kA01",
    "module-2": "https://buy.stripe.com/bJeeVdeqDbOmfV1b2U1kA02",
    "module-3": "https://buy.stripe.com/6oUdR94Q319I1077QI1kA03",
    "module-4": "https://buy.stripe.com/9B614n4Q33hQ6kr3As1kA04",
    "module-5": "https://buy.stripe.com/fZu3cv82f6u24cjef61kA09",
    "module-6": "https://buy.stripe.com/6oUfZh5U7bOmeQX7QI1kA0a",
    "all-six": "https://buy.stripe.com/fZu28raan3hQeQXfja1kA07",
    "site-license": "https://buy.stripe.com/eVq8wPaan2dM4cj1sk1kA08",
};

export default function Courses() {
    const [courses, setCourses] = useState([]);
    const [bundles, setBundles] = useState([]);
    const { add, has, count } = useCart();

    useEffect(() => {
        api.get("/courses").then(({ data }) => {
            setCourses(data.courses);
            setBundles(data.bundles);
        });
    }, []);

    const handleAdd = (course) => {
        const added = add(course.id);
        if (added) toast.success(`Added ${course.title} to cart`);
        else toast.message(`${course.title} is already in your cart`);
    };

    return (
        <div className="bg-background">
            <section className="border-b border-black/10">
                <div className="container-edge pt-16 pb-12 lg:pt-24">
                    <p className="overline">Catalog</p>
                    <h1 className="mt-3 font-heading font-black text-5xl lg:text-6xl tracking-tighter leading-[0.95] max-w-3xl">
                        Buy a module. Or take the whole curriculum to your school.
                    </h1>
                    <p className="mt-6 max-w-2xl text-muted-foreground text-lg leading-relaxed">
                        All purchases unlock immediate access in your dashboard. Schools — see bulk pricing below.
                    </p>
                </div>
            </section>

            {/* INDIVIDUAL MODULES */}
            <section className="border-b border-black/10">
                <div className="container-edge py-16">
                    <div className="flex items-end justify-between mb-10">
                        <div>
                            <p className="overline">Individual modules</p>
                            <h2 className="mt-2 font-heading font-bold text-3xl tracking-tight">Buy what you need</h2>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-black/10 border border-black/10">
                        {courses.map((c) => (
                            <article
                                key={c.id}
                                data-testid={`course-card-${c.id}`}
                                className="bg-white p-8 flex flex-col"
                            >
                                <div className="flex items-start justify-between">
                                    <span className="font-mono text-xs tracking-widest text-klein">MOD / {c.number}</span>
                                    <span className="text-xs uppercase tracking-widest opacity-60">{c.duration}</span>
                                </div>
                                <h3 className="mt-5 font-heading font-bold text-2xl tracking-tight leading-tight">
                                    {c.title}
                                </h3>
                                <p className="mt-1 text-xs uppercase tracking-widest text-klein">{c.tagline}</p>
                                <p className="mt-4 text-sm text-muted-foreground leading-relaxed">{c.description}</p>
                                <ul className="mt-5 space-y-2">
                                    {c.topics.map((t) => (
                                        <li key={t} className="flex items-start gap-2 text-sm">
                                            <Check size={14} className="mt-1 text-klein shrink-0" /> {t}
                                        </li>
                                    ))}
                                </ul>
                                <div className="mt-auto pt-8">
                                    <div className="flex items-center justify-between">
                                        <span className="font-heading font-black text-3xl">${c.price}</span>
                                        <a
                                            data-testid={`buy-course-${c.id}`}
                                            href={STRIPE_LINKS[c.id]}
                                            className="text-xs font-medium text-klein hover:text-black underline underline-offset-4"
                                        >
                                            Buy now
                                        </a>
                                    </div>
                                    <button
                                        onClick={() => handleAdd(c)}
                                        disabled={has(c.id)}
                                        data-testid={`add-cart-${c.id}`}
                                        className={`mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold transition-colors ${
                                            has(c.id)
                                                ? "bg-black text-white cursor-not-allowed"
                                                : "bg-klein text-white hover:bg-black"
                                        }`}
                                    >
                                        {has(c.id) ? (<><CheckCircle2 size={14} /> In cart</>) : (<><PlusCircle size={14} /> Add to cart</>)}
                                    </button>
                                </div>
                            </article>
                        ))}
                    </div>
                    {count > 0 && (
                        <div className="mt-8 flex items-center justify-between border border-klein bg-klein/5 p-5" data-testid="courses-cart-banner">
                            <div className="text-sm">
                                <strong>{count}</strong> module{count === 1 ? "" : "s"} in your cart. Check out once for everything.
                            </div>
                            <Link
                                to="/cart"
                                data-testid="courses-view-cart"
                                className="inline-flex items-center gap-2 px-5 py-2.5 bg-klein text-white text-sm font-semibold hover:bg-black transition-colors"
                            >
                                <ShoppingCart size={14} /> View cart
                            </Link>
                        </div>
                    )}
                </div>
            </section>

            {/* COMMUNITY — soft signup below individual modules; does not replace Buy / Add-to-cart */}
            <CommunitySection variant="soft" />

            {/* BUNDLES */}
            <section className="bg-secondary">
                <div className="container-edge py-20">
                    <p className="overline">For departments & schools</p>
                    <h2 className="mt-3 font-heading font-black text-4xl lg:text-5xl tracking-tighter">
                        Bulk pricing
                    </h2>

                    <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-px bg-black/10 border border-black/10">
                        {bundles.map((b, i) => (
                            <div
                                key={b.id}
                                data-testid={`bundle-card-${b.id}`}
                                className={`p-10 flex flex-col ${i === 1 ? "bg-klein text-white" : "bg-white"}`}
                            >
                                <p className={`overline ${i === 1 ? "text-white/70" : ""}`}>
                                    {i === 1 ? "School plan" : "Faculty plan"}
                                </p>
                                <h3 className="mt-3 font-heading font-bold text-3xl tracking-tight">{b.title}</h3>
                                <p className={`mt-1 text-sm ${i === 1 ? "text-white/80" : "text-muted-foreground"}`}>
                                    {b.subtitle}
                                </p>
                                <div className="mt-8 font-heading font-black text-5xl tracking-tighter">${b.price}</div>
                                <ul className={`mt-8 space-y-2 ${i === 1 ? "text-white/90" : ""}`}>
                                    <li className="flex items-start gap-2 text-sm"><Check size={14} className={`mt-1 shrink-0 ${i === 1 ? "text-white" : "text-klein"}`} /> All 6 modules included</li>
                                    <li className="flex items-start gap-2 text-sm"><Check size={14} className={`mt-1 shrink-0 ${i === 1 ? "text-white" : "text-klein"}`} /> Templates & prompt library</li>
                                    {i === 1 && <li className="flex items-start gap-2 text-sm"><Check size={14} className="mt-1 text-white shrink-0" /> Up to 50 faculty seats</li>}
                                    {i === 1 && <li className="flex items-start gap-2 text-sm"><Check size={14} className="mt-1 text-white shrink-0" /> Priority workshop scheduling</li>}
                                </ul>
                                <a
                                    data-testid={`buy-bundle-${b.id}`}
                                    href={STRIPE_LINKS[b.id]}
                                    className={`mt-10 inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold transition-colors ${
                                        i === 1
                                            ? "bg-white text-klein hover:bg-black hover:text-white"
                                            : "bg-klein text-white hover:bg-black"
                                    }`}
                                >
                                    <ShoppingBag size={16} /> Buy {b.title}
                                </a>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
}
